/* eslint-disable tailwindcss/no-arbitrary-value, react/require-default-props */
import React, { useState, useContext, Fragment } from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import {
  FormGroup,
  Radio,
  SelectionControlLabel,
  Card,
  CardContent,
  CardTitle,
  Typography
} from '@one-thd/sui-atomic-components';
import { Add } from '@one-thd/sui-icons';
import { extend } from '@thd-nucleus/data-sources';
import { isProductUnavailable, getUnavailableMessage } from '../../utils/product-unavailable-utils';
import { ProductListItem } from './ProductListItem';
import { CustomSkeletonLine } from './CustomSkeletonLine';
import {
  LAUNDRY_ACCESSORIES_DRAWER,
  NO_ACCESSORIES,
  PRICE_TYPES
} from '../../constants/index';
import { InfoDrawer } from './infoDrawer/InfoDrawer';
import { BundleAlertmessage } from './BundleAlertmessage';

const AccessoryListContent = ({ accessories = [], loading, addAccessories, product: bundleProduct = undefined }) => {
  const [selectedOffering, setSelectedOffering] = useState(NO_ACCESSORIES);

  const { configService = {} } = useContext(ExperienceContext) || {};

  const configIsAccessoriesFeatureEnabled = useConfigService('fs:isAccessoriesFeatureEnabled') || null;
  const enableAccessories = configIsAccessoriesFeatureEnabled || configService['fs:isAccessoriesFeatureEnabled'];
  if (!enableAccessories) {
    return null;
  }

  if (loading) {
    // TODO: Address any CLS concerns with an appropriate placeholder
    return (
      <div
        className="sui-flex sui-flex-col sm:sui-mx-4"
        data-testid="accessory-list-placeholder"
      >
        <CustomSkeletonLine height="25px" />
      </div>
    );
  }

  if (!accessories || accessories.length === 0) {
    return null;
  }

  const accessoriesData = accessories[0];

  const handleAccessoryChange = (event) => {
    const selectedAccessory = event?.target?.value;
    const isNoAccessoriesSelected = selectedAccessory === NO_ACCESSORIES;
    setSelectedOffering(isNoAccessoriesSelected ? NO_ACCESSORIES : `offering-${selectedAccessory}`);
    const accesoriesData = isNoAccessoriesSelected ? null : accessoriesData?.offerings?.[selectedAccessory];
    addAccessories(accesoriesData);
  };

  return (
    <>
      <Card disablePadding className="sui-rounded-sm" data-testid="accessory-list-content">
        <CardContent orientation="vertical">
          <div className="sui-flex sui-items-center sui-gap-2">
            <CardTitle
              headerVariant="h5"
              header="Stacking & Storage Solutions"
              headerWeight="bold"
            />
            <InfoDrawer variantNames={[LAUNDRY_ACCESSORIES_DRAWER]} />
          </div>
          <FormGroup orientation="vertical" gap>
            <div>
              <SelectionControlLabel label="None" className="sui-font-bold sui-text-lg">
                <Radio
                  checked={selectedOffering === NO_ACCESSORIES}
                  onChange={handleAccessoryChange}
                  value={NO_ACCESSORIES}
                  name="accessory-selection"
                  inputAttributes={{ 'aria-label': 'None' }}
                />
              </SelectionControlLabel>
            </div>
            <hr className="sui-my-2" />
            {accessoriesData.offerings?.map((offering, offeringIndex) => {
              // Check if any product in the offering is unavailable
              const unavailableProduct = offering.products?.find((product) =>
                isProductUnavailable({ product })
              );

              const isOfferingUnavailable = !!unavailableProduct;
              const alertMessage = unavailableProduct
                ? getUnavailableMessage({ product: unavailableProduct, bundleProduct })
                : null;

              return (
                <Fragment key={offeringIndex}>
                  <div
                    className="sui-flex sui-flex-col sui-gap-4"
                  >
                    {isOfferingUnavailable && alertMessage && (
                      <div className="sui-mb-2">
                        <BundleAlertmessage
                          status="error"
                          alertSize="small"
                          content={{
                            main: alertMessage
                          }}
                        />
                      </div>
                    )}
                    <div className="sui-w-full">
                      <SelectionControlLabel label={offering.offeringName}>
                        <Radio
                          checked={selectedOffering === `offering-${offeringIndex}`}
                          onChange={handleAccessoryChange}
                          value={offeringIndex}
                          name="accessory-selection"
                          inputAttributes={{ 'aria-label': offering.offeringName }}
                          disabled={isOfferingUnavailable}
                        />
                      </SelectionControlLabel>
                      {
                        offering?.description && (
                          <div className="sui-ml-[29px]">
                            <Typography variant="body-base">
                              {offering.description}
                            </Typography>
                          </div>
                        )
                      }
                    </div>
                    {offering.products?.map((product, productIndex) => {
                      return (
                        <Fragment key={`accessory-${product.itemId}`}>
                          <div className="sui-w-full sui-pl-[29px]">
                            <ProductListItem product={product} variant="accessory" priceType={PRICE_TYPES.MINIMAL} />
                          </div>

                          {offering.products.length > 1
                            && productIndex < offering.products.length - 1 && (
                            <div className="sui-self-center"><Add size="small" aria-hidden="true" /></div>) }
                        </Fragment>
                      );
                    })}
                  </div>
                  {offeringIndex < accessoriesData.offerings.length - 1 && <hr className="sui-my-2" />}
                </Fragment>
              );
            })}
          </FormGroup>
        </CardContent>
      </Card>
    </>
  );
};

AccessoryListContent.propTypes = {
  accessories: PropTypes.arrayOf(
    PropTypes.shape({
      categoryName: PropTypes.string.isRequired,
      offerings: PropTypes.arrayOf(
        PropTypes.shape({
          offeringName: PropTypes.string.isRequired,
          products: PropTypes.arrayOf(PropTypes.shape({
            itemId: PropTypes.string,
            quantity: PropTypes.number,
            type: PropTypes.string
          }))
        })
      )
    })
  ),
  loading: PropTypes.bool.isRequired,
  addAccessories: PropTypes.func.isRequired,
  product: PropTypes.shape({
    bundleSpecificationDetails: PropTypes.shape({
      type: PropTypes.string
    }),
    features: PropTypes.shape({
      showProductUpdateAlert: PropTypes.bool,
      showProductUpdateGeneralAlert: PropTypes.bool,
      templates: PropTypes.shape({
        [PropTypes.string]: PropTypes.string
      })
    }),
    productComponent: PropTypes.arrayOf(
      PropTypes.shape({
        selectableVariants: PropTypes.shape({
          [PropTypes.string]: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string.isRequired,
              value: PropTypes.string.isRequired
            })
          )
        })
      })
    )
  })
};

AccessoryListContent.dataModel = extend(ProductListItem);

export { AccessoryListContent };
